/* A disclosure of real buttons: selecting a size adds that exact variant once. */
(() => {
  if (customElements.get('gc-quick-add')) return;
  let menuId = 0;

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.trigger = this.querySelector('.gc-quick-add__trigger');
      this.menu = this.querySelector('.gc-quick-add__menu');
      if (this.menu && this.trigger) {
        this.menu.id = `gc-size-menu-${++menuId}`;
        this.trigger.setAttribute('aria-controls', this.menu.id);
        this.setOpen(false);
      }
      this.addEventListener('click', (event) => {
        if (event.target.closest('.gc-quick-add__trigger')) {
          this.setOpen(this.trigger.getAttribute('aria-expanded') !== 'true');
          return;
        }
        const button = event.target.closest('[data-variant-id]');
        if (button && !button.disabled) this.addVariant(button.dataset.variantId);
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (!this.menu) return;
        if (event.key === 'Escape' && !this.menu.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.setOpen(false, true);
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        if (this.menu.hidden && event.target !== this.trigger) return;
        event.preventDefault();
        this.setOpen(true);
        const options = [...this.querySelectorAll('.gc-quick-add__option:not(:disabled)')];
        if (!options.length) return;
        const index = options.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 :
          event.key === 'ArrowDown' ? (index + 1) % options.length : (index < 0 ? options.length - 1 : (index - 1 + options.length) % options.length);
        options[next].focus();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        // Use the destination, not activeElement during the blur/focus transition.
        // A microtask here can hide the size button before its click is delivered.
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.setOpen(false);
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      }, { signal });
      document.addEventListener('gc:quick-add-open', (event) => {
        if (event.detail !== this) this.setOpen(false);
      }, { signal });
      queueMicrotask(() => {
        if (!this.isConnected || signal.aborted) return;
        this.querySelector('.gc-quick-add__control')?.removeAttribute('hidden');
        this.querySelector('.gc-quick-add__fallback')?.setAttribute('hidden', '');
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.setOpen(false);
      // Do not abort an in-flight cart request: its result may already be committed.
    }

    setOpen(open, restoreFocus = false) {
      if (!this.menu || !this.trigger) return;
      this.menu.hidden = !open;
      this.trigger.setAttribute('aria-expanded', String(open));
      this.closest('.product-card')?.classList.toggle('gc-quick-add-open', open);
      if (open) document.dispatchEvent(new CustomEvent('gc:quick-add-open', { detail: this }));
      if (restoreFocus && this.isConnected) this.trigger.focus({ preventScroll: true });
    }

    showStatus(message, error = false) {
      const status = this.querySelector('.gc-quick-add__status');
      if (status) {
        status.textContent = message;
        status.classList.toggle('is-error', error);
      }
    }

    async openUpcart() {
      if (typeof window.upcartOpenCart !== 'function' || typeof window.upcartRefreshCart !== 'function') return false;
      try {
        await window.upcartRefreshCart();
        window.upcartOpenCart();
        return true;
      } catch (_) {
        // Keep the successful addition and let the native cart provide a fallback.
        return false;
      }
    }

    async addVariant(variantId) {
      if (this.pending || !/^\d+$/.test(variantId)) return;
      this.pending = true;
      const controls = [...this.querySelectorAll('button')].map((control) => ({ control, disabled: control.disabled }));
      controls.forEach(({ control }) => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.showStatus(this.dataset.loading);
      const root = window.Shopify?.routes?.root || '/';
      const sections = [];
      const broadcast = (name, detail) => document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      broadcast('theme:loading:start');
      broadcast('cart:prepare-bundled-sections', { sections });
      let added = false;
      try {
        const data = new FormData();
        data.set('id', variantId);
        data.set('quantity', '1');
        if (sections.length) data.set('sections', [...new Set(sections)].slice(0, 5).join(','));
        data.set('sections_url', window.location.pathname);
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', body: data, credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
        });
        const result = await response.json();
        if (!response.ok) {
          this.showStatus(typeof result.description === 'string' ? result.description : this.dataset.error, true);
          this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: this.querySelector('.gc-quick-add__status')?.textContent } }));
          broadcast('cart:refresh');
          return;
        }
        added = true;
        const restoreFocus = this.contains(document.activeElement);
        if (this.trigger) this.trigger.disabled = false;
        this.setOpen(false, restoreFocus);
        this.showStatus('');
        const hasUpcart = typeof window.upcartOpenCart === 'function' && typeof window.upcartRefreshCart === 'function';
        if (!hasUpcart && window.themeVariables?.settings?.cartType === 'page') {
          window.location.assign(`${root}cart`);
          return;
        }
        const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const cart = await cartResponse.json();
        cart.sections = result.sections || {};
        const upcartOpened = await this.openUpcart();
        const detail = {
          items: result.items || [result], cart, onSuccessDo: upcartOpened ? null : 'force_open_drawer'
        };
        this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail }));
        if (sections.some((id) => !result.sections?.[id])) {
          // Prestige requires valid section markup; refresh rather than hand it null.
          broadcast('cart:refresh');
        } else {
          broadcast('cart:change', { ...detail, baseEvent: upcartOpened ? 'gc:variant:add' : 'variant:add' });
        }
      } catch (_) {
        // Never retry a POST automatically: a lost response can follow a successful add.
        this.showStatus(added ? '' : this.dataset.uncertain, !added);
        if (added) await this.openUpcart();
        broadcast('cart:refresh');
      } finally {
        this.pending = false;
        this.removeAttribute('aria-busy');
        controls.forEach(({ control, disabled }) => { control.disabled = disabled; });
        broadcast('theme:loading:end');
      }
    }
  }
  customElements.define('gc-quick-add', QuickAdd);
})();
